import { useState } from 'react';
import { useAtom } from 'jotai';
import { X } from 'lucide-react';
import { currentProjectAtom } from '@/stores/editorStore';
import { apiRequest } from '@/lib/api';
import type { Project, ProjectTemplate } from '@shared/types';

interface NewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectCreated?: (optimisticProject: Project) => void;
}

const templates: { value: ProjectTemplate; label: string; description: string }[] = [
  { value: 'vite-react-ts', label: 'React + TypeScript', description: 'React with Vite and TypeScript' },
  { value: 'vite-react-supabase-ts', label: 'React + Supabase', description: 'React with TypeScript and Supabase client' },
  { value: 'vite-vue-ts', label: 'Vue + TypeScript', description: 'Vue 3 with Vite and TypeScript' },
  { value: 'vite-svelte-ts', label: 'Svelte + TypeScript', description: 'Svelte with Vite and TypeScript' },
  { value: 'vite-react-js', label: 'React', description: 'React with Vite and JavaScript' },
  { value: 'next-ts', label: 'Next.js + TypeScript', description: 'Next.js with TypeScript' },
];

export default function NewProjectModal({ isOpen, onClose, onProjectCreated }: NewProjectModalProps) {
  const [, setCurrentProject] = useAtom(currentProjectAtom);
  const [mode, setMode] = useState<'quick' | 'advanced'>('quick');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [template, setTemplate] = useState<ProjectTemplate>('vite-react-ts');
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("[NewProjectModal] Form submitted");
    setError(null);

    if (!name.trim()) {
      console.log("[NewProjectModal] Validation failed: name is required");
      setError('Project name is required');
      return;
    }

    // Create optimistic project
    const optimisticProject: Project & { _isCreating?: boolean } = {
      id: `temp-${Date.now()}`,
      user_id: 'current',
      name: name.trim(),
      description: description.trim() || undefined,
      path: '',
      template,
      dev_server_status: 'installing',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _isCreating: true,
    };

    // Call callback to add to projects list
    onProjectCreated?.(optimisticProject);

    // Close dialog immediately
    const savedName = name.trim();
    const savedDescription = description.trim() || undefined;
    const savedTemplate = template;
    setName('');
    setDescription('');
    setTemplate('vite-react-ts');
    onClose();

    // Create project in background
    try {
      console.log("[NewProjectModal] Making API request:", { name: savedName, template: savedTemplate });
      const response = await apiRequest<{ project: Project }>('post', '/projects', {
        name: savedName,
        description: savedDescription,
        template: savedTemplate,
      });

      // Update with real project data
      setCurrentProject(response.project);
      onProjectCreated?.(response.project);
    } catch (err) {
      console.error("[NewProjectModal] Failed to create project:", err);
      // Clear current project on error
      setCurrentProject(null);
      // Show error to user
      alert(err instanceof Error ? err.message : 'Failed to create project');
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setTemplate('vite-react-ts');
    setMode('quick');
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Create New Project</h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-muted rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Mode Toggle */}
          <div className="flex gap-2 p-1 bg-muted/30 rounded-lg">
            <button
              type="button"
              onClick={() => setMode('quick')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                mode === 'quick'
                  ? 'bg-background shadow-sm'
                  : 'hover:bg-muted/50'
              }`}
            >
              Quick Start
            </button>
            <button
              type="button"
              onClick={() => setMode('advanced')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                mode === 'advanced'
                  ? 'bg-background shadow-sm'
                  : 'hover:bg-muted/50'
              }`}
            >
              Advanced
            </button>
          </div>

          {/* Project Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-2">
              Project Name *
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="my-awesome-project"
              className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium mb-2">
              {mode === 'quick' ? 'What do you want to build? (optional)' : 'Description (optional)'}
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                mode === 'quick'
                  ? 'e.g., A todo app with dark mode and local storage'
                  : 'What is this project about?'
              }
              rows={3}
              className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          {/* Template (only in advanced mode) */}
          {mode === 'advanced' && (
            <div>
              <label htmlFor="template" className="block text-sm font-medium mb-2">
                Template
              </label>
              <div className="grid grid-cols-2 gap-3">
                {templates.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setTemplate(t.value)}
                    className={`p-3 border rounded-md text-left transition-colors ${
                      template === t.value
                        ? 'border-primary bg-primary/10'
                        : 'hover:border-muted-foreground/50'
                    }`}
                  >
                    <p className="font-medium text-sm">{t.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quick mode hint */}
          {mode === 'quick' && (
            <div className="p-3 bg-muted/50 rounded-md">
              <p className="text-sm text-muted-foreground">
                Your project will use React + TypeScript. Switch to Advanced mode to choose a different template.
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm border rounded-md hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Create Project
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
